import type { History } from "history";
import CompanyService from "../services/CompanyService";
import AuthenticationService, {
  ValidLogonResponse
} from "../services/AuthenticationService";
import { StorageService } from "../services/StorageService";

// ---------- Query params ----------
export function getQueryParams(search: string) {
  const qp = new URLSearchParams(search);
  return {
    token: qp.get("token") ?? "",
    email: qp.get("email") ?? "",
    remember: qp.get("remember") === "ok",
    returnUrl: qp.get("returnUrl"),
    guid: qp.get("guid") ?? qp.get("gid") ?? ""
  };
}

// ---------- Cultura (idioma) ----------
export function getCulture(currentLanguage: string) {
  return (
    CompanyService.getCompanyToken()?.culture.split("-")[0] || currentLanguage
  );
}

// ---------- Redirecciones ----------
export function redirectToReturnUrl(opts: {
  history: History;
  location: Location;
  culture: string;
  overrideReturnUrl?: string | null;
  reload?: boolean;
}) {
  const { history, location, culture, overrideReturnUrl, reload } = opts;
  const raw =
    overrideReturnUrl ?? new URLSearchParams(location.search).get("returnUrl");
  const fixed = raw?.replace("/es/", `/${culture}/`);
  const isExternalUrl = isExternalReturnUrl(fixed);

  if (fixed && isExternalUrl) {
    const refreshToken = CompanyService.getCompanyAuth()?.refreshToken;
    window.location.href = `${fixed}?token=${refreshToken}`;
    return;
  }
  if (fixed) {
    history.push(fixed);
  } else {
    history.push(`/${culture}/company`);
  }
  if (reload) window.location.reload();
}

// TODO: review this new condition used form redirection to CashApp.
export function isExternalReturnUrl(returnUrl: string | null): boolean {
  return returnUrl && returnUrl.startsWith("http");
}

export function redirectToExternalUrl(location: Location) {
  const base = `https://www1.intiza.com/account/logontoken?token=${CompanyService.getCompanyAuth()?.companyToken}`;
  const ret = location.pathname + location.search;
  window.location.href = ret ? `${base}&${ret}` : base;
}

export async function routeByVersion(opts: {
  history: History;
  location: Location;
  doLogout: () => Promise<void> | void;
  currentLanguage: string;
  overrideReturnUrl?: string;
  reloadOnInternal?: boolean;
}) {
  const {
    history,
    location,
    doLogout,
    currentLanguage,
    overrideReturnUrl,
    reloadOnInternal
  } = opts;
  const versionAllowed = CompanyService.getVersionAllowed();
  const userCurrentVersion = CompanyService.getUserCurrentVersion();
  const culture = getCulture(currentLanguage);

  if (versionAllowed === 2) {
    redirectToReturnUrl({
      history,
      location,
      culture,
      overrideReturnUrl,
      reload: reloadOnInternal
    });
    return;
  }

  if (versionAllowed === 3) {
    if (userCurrentVersion !== 2) {
      redirectToExternalUrl(location);
    } else {
      redirectToReturnUrl({
        history,
        location,
        culture,
        overrideReturnUrl,
        reload: reloadOnInternal
      });
    }
    return;
  }

  await Promise.resolve(doLogout());
  redirectToExternalUrl(location);
}

// ---------- Login de compañía (compartido) ----------
export async function doCompanyLogin(opts: {
  userData: ValidLogonResponse;
  remember: boolean;
  setCurrentAuthentication: (x: any) => void;
  cacheEmail?: string;
}) {
  const { userData, remember, setCurrentAuthentication, cacheEmail } = opts;
  const companyData = await AuthenticationService.companyLogin(
    userData,
    remember
  );
  if (companyData instanceof Error) throw companyData;

  StorageService.setPreference("rememberUser", remember);
  if (typeof cacheEmail === "string") {
    StorageService.setPreference("userEmailCache", remember ? cacheEmail : "");
  }

  setCurrentAuthentication(companyData);
}

// ---------- URLs especiales de 2AF y password expired ----------
export function build2AFUrl(
  currentLanguage: string,
  search: string,
  twofadata: string,
  email: string,
  remember: boolean
) {
  const qp = new URLSearchParams(search);
  qp.append("token", twofadata);
  qp.append("email", email);
  qp.append("remember", remember ? "ok" : "nok");
  return `/${currentLanguage}/completesignin?${qp.toString()}`;
}

export function buildPasswordExpiredUrl(
  currentLanguage: string,
  search: string,
  expiredToken: string,
  email: string
) {
  const qp = new URLSearchParams(search);
  qp.append("token", expiredToken);
  qp.append("email", email);
  return `/${currentLanguage}/account/passwordexpired?${qp.toString()}`;
}
